/**
 * Gesvalt Formularios - Form Validation and Submission Handler
 * Maneja la validación del formulario y el estado del botón submit
 */

class GesvaltFormHandler {
    constructor() {
        this.forms = [];
        this.validationRules = {
            required: (value) => value && value.trim() !== '',
            email: (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
            phone: (value) => !value || /^[+]?[0-9\s\-\(\)]{9,}$/.test(value),
            min_length: (value, min) => !value || value.length >= parseInt(min),
            max_length: (value, max) => !value || value.length <= parseInt(max),
            numeric: (value) => !value || /^[0-9]+$/.test(value),
            url: (value) => !value || /^https?:\/\/.+/.test(value),
            postal_code: (value) => !value || /^[0-9]{5}$/.test(value),
            // Nueva regla: accepted - para checkboxes de consentimiento
            accepted: (value, param, field) => {
                // Para checkboxes, verificar que esté marcado
                if (field && field.type === 'checkbox') {
                    return field.checked;
                }
                // Para otros campos, verificar valores que indican aceptación
                return value && ['1', 'true', 'yes', 'on'].includes(value.toString().toLowerCase());
            },
            // Nueva regla: date - validación básica de fechas
            date: (value) => {
                if (!value) return true; // Campo opcional

                // Intentar parsear la fecha
                const date = new Date(value);

                // Verificar que sea una fecha válida
                if (isNaN(date.getTime())) {
                    return false;
                }

                // Verificar formato básico (YYYY-MM-DD, DD/MM/YYYY, etc.)
                const dateRegex = /^(\d{4}-\d{2}-\d{2})|(\d{2}\/\d{2}\/\d{4})|(\d{2}-\d{2}-\d{4})$/;
                return dateRegex.test(value);
            }
        };

        this.init();
    }

    init() {
        // Inicializar cuando el DOM esté listo
        this.bindForms();

    }

    getBaseLang() {
        const lang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
        if (!lang) return '';
        const parts = lang.split('-');
        return (parts[0] || '').toLowerCase();
    }

    isFieldApplicableByLanguage(field) {
        const container = this.getFieldContainer(field);
        const required = (container?.dataset?.visibleLang || '').toLowerCase();
        if (!required) return true;
        const base = this.getBaseLang();
        return required === base;
    }

    bindForms() {
        // Buscar todos los formularios de Gesvalt
        const forms = document.querySelectorAll('form.gesvalt-form');

        forms.forEach(form => {
            this.bindForm(form);
        });

        // Observer para formularios que se añadan dinámicamente
        this.observeNewForms();
    }

    bindForm(form) {
        if (form.dataset.gesvaltBound) return;

        form.dataset.gesvaltBound = 'true';
        this.forms.push(form);

        console.log('Binding form:', form);

        // Bind submit event
        form.addEventListener('submit', (e) => this.handleSubmit(e));

        // Bind real-time validation
        this.bindFieldValidation(form);

        // Bind field dependencies
        this.bindFieldDependencies(form);

        console.log('Gesvalt form bound:', form);
    }

    bindFieldValidation(form) {
        const fields = form.querySelectorAll('[data-gesfor-validate-rules]');

        fields.forEach(field => {
            // Validar en blur y input
            field.addEventListener('blur', () => this.validateField(field));
            field.addEventListener('input', () => this.clearFieldError(field));
        });
    }

    async handleSubmit(event) {
        const form = event.target;
        const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');

        // Evitar múltiples envíos
        if (form.dataset.gesvaltSubmitting === 'true') {
            event.preventDefault();
            event.stopImmediatePropagation?.();
            return;
        }

        // 1. Validación del formulario
        if (!this.validateForm(form)) {
            event.preventDefault();
            this.showValidationSummary(form);
            this.trackValidationErrors(form);
            return;
        }

        const rcConf = window.GesvaltFormulariosConf?.recaptcha || {};
        const usingV3 = rcConf.enabled && rcConf.implementation === 'google_v3';

        // Asegurar que existe el input oculto del token
        let tokenInput = form.querySelector('[name="g-recaptcha-response"]');
        

        // 2. Manejo de reCAPTCHA v3 (async)
        if (usingV3) {
            event.preventDefault();
            event.stopImmediatePropagation?.();

            try {
                const siteKey = rcConf.siteKey;
                const action = rcConf.action || 'form_submit';

                if (!window.grecaptcha?.execute || !siteKey) {
                    alert('reCAPTCHA no está disponible');
                    return;
                }

                const token = await window.grecaptcha.execute(siteKey, { action });
                tokenInput.value = token || '';
            } catch (err) {
                alert('Error al verificar reCAPTCHA');
                return;
            }
        }

        console.log('Form submission 3:', tokenInput);

        // 3. Validar reCAPTCHA checkbox (si aplica)
        if (!tokenInput.value.trim()) {
            event.preventDefault();
            alert('Por favor, verifica el reCAPTCHA antes de enviar.');
            document.querySelector('.g-recaptcha')?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            return;
        }

        // 4. Tracking + estado de envío
        form.dataset.gesvaltSubmitting = 'true';
        this.trackFormSubmission(form);
        this.setSubmitState(submitBtn, 'loading');

        // 5. Envío tradicional
        if (usingV3) {
            // Si es v3 evitamos interferencias y forzamos envío manual
            form.submit();
        }
        // Caso checkbox: dejamos que el submit natural siga su curso
    }

    // 🎯 Método para tracking de envío de formulario (simplificado)
    trackFormSubmission(form) {
        // Verificar si GTM tracker está disponible
        if (typeof window.gesvaltGTMTracker !== 'undefined') {
            try {
                // Usar el método del tracker GTM que ya maneja la extracción de datos
                window.gesvaltGTMTracker.trackFormSubmission(form);
            } catch (error) {
                console.warn('Error en tracking GTM:', error);
            }
        }
    }

    // 🎯 Método para tracking de errores de validación (simplificado)
    trackValidationErrors(form) {
        if (typeof window.gesvaltGTMTracker !== 'undefined') {
            try {
                const errorFields = form.querySelectorAll('.gesfor-field-error');
                const errors = Array.from(errorFields).map(field => {
                    return this.getFieldLabel(field) || field.name || 'Campo sin nombre';
                });

                if (errors.length > 0) {
                    window.gesvaltGTMTracker.trackValidationError(form, errors);
                }
            } catch (error) {
                console.warn('Error en tracking de validación GTM:', error);
            }
        }
    }

    validateForm(form) {
        const fields = form.querySelectorAll('[data-gesfor-validate-rules]');
        let isValid = true;
        const errors = [];

        fields.forEach(field => {
            if (this.isFieldHiddenByDependency(field) || !this.isFieldApplicableByLanguage(field)) {
                return;
            }

            const fieldValid = this.validateField(field);
            if (!fieldValid) {
                isValid = false;
                const label = this.getFieldLabel(field);
                errors.push(label || field.name || 'Campo sin nombre');
            }
        });

        return isValid;
    }

    validateField(field) {
        if (!this.isFieldApplicableByLanguage(field)) return true;
        const rules = field.dataset.gesforValidateRules;
        if (!rules) return true;

        const value = field.value;
        const rulesArray = rules.split('|');
        let isValid = true;
        const errors = [];

        rulesArray.forEach(rule => {
            const [ruleName, ruleParam] = rule.split(':');

            if (this.validationRules[ruleName]) {
                // Pasar el campo como tercer parámetro para reglas que lo necesiten
                const valid = this.validationRules[ruleName](value, ruleParam, field);
                if (!valid) {
                    isValid = false;
                    errors.push(this.getErrorMessage(ruleName, ruleParam));
                }
            }
        });

        // Mostrar/ocultar error en el campo
        this.displayFieldError(field, isValid ? null : errors[0]);

        return isValid;
    }

    getErrorMessage(ruleName, param) {
        const i18n = window.GesvaltFormulariosConf?.i18n || {};

        const messages = {
            required: i18n.required_field || 'Este campo es obligatorio',
            email: i18n.invalid_email || 'Introduce un email válido',
            phone: i18n.invalid_phone || 'Introduce un teléfono válido',
            min_length: i18n.min_length ? i18n.min_length.replace('%d', param) : `Mínimo ${param} caracteres`,
            max_length: i18n.max_length ? i18n.max_length.replace('%d', param) : `Máximo ${param} caracteres`,
            numeric: i18n.numeric || 'Solo se permiten números',
            url: i18n.invalid_url || 'Introduce una URL válida',
            postal_code: i18n.invalid_postal_code || 'Introduce un código postal válido',
            accepted: i18n.accepted_required || 'Debes aceptar este campo para continuar',
            date: i18n.invalid_date || 'Introduce una fecha válida',
            file_required: i18n.file_required || 'Debes seleccionar un archivo',
            file_too_large: i18n.file_too_large || 'El archivo es demasiado grande',
            file_invalid_type: i18n.file_invalid_type || 'Tipo de archivo no válido'
        };

        return messages[ruleName] || i18n.invalid_field || 'Campo inválido';
    }

    displayFieldError(field, error) {
        // Limpiar errores previos
        this.clearFieldError(field);

        if (error) {
            field.classList.add('gesfor-field-error');

            // Crear mensaje de error
            const errorElement = document.createElement('div');
            errorElement.className = 'gesfor-error-message';
            errorElement.innerHTML = error;

            console.log("ERROR Field ", field.name, error);

            // Para checkboxes, insertar después del contenedor padre
            if (field.type === 'checkbox') {
                // Buscar el contenedor padre que incluye checkbox + label + asterisco
                const parentContainer = field.parentNode;
                parentContainer.parentNode.insertBefore(errorElement, parentContainer.nextSibling);
            } else {
                // Para otros campos, insertar después del campo
                field.parentNode.insertBefore(errorElement, field.nextSibling);
            }
        }
    }

    clearFieldError(field) {
        field.classList.remove('gesfor-field-error');

        // Para checkboxes, buscar el error en el nivel del contenedor padre
        if (field.type === 'checkbox') {
            const parentContainer = field.parentNode;
            const errorMessage = parentContainer.parentNode.querySelector('.gesfor-error-message');
            if (errorMessage) {
                errorMessage.remove();
            }
        } else {
            // Para otros campos, buscar en el contenedor directo
            const errorMessage = field.parentNode.querySelector('.gesfor-error-message');
            if (errorMessage) {
                errorMessage.remove();
            }
        }
    }

    displayErrors(form, errors) {
        // Limpiar errores previos
        this.clearErrors(form);

        if (errors.length > 0) {
            const i18n = window.GesvaltFormulariosConf?.i18n || {};
            const errorContainer = document.createElement('div');
            errorContainer.className = 'gesfor-validation-errors';
            errorContainer.innerHTML = `
                <h4>${i18n.validation_errors_title || 'Por favor, corrige los siguientes errores:'}</h4>
                <ul>${errors.map(error => `<li>${error}</li>`).join('')}</ul>
            `;

            form.insertBefore(errorContainer, form.firstChild);
        }
    }

    clearErrors(form) {
        const errorContainer = form.querySelector('.gesfor-validation-errors');
        if (errorContainer) {
            errorContainer.remove();
        }
    }

    showValidationSummary(form) {
        // Mostrar resumen de validación si hay errores
        const errorFields = form.querySelectorAll('.gesfor-field-error');
        if (errorFields.length > 0) {
            const firstError = errorFields[0];
            firstError.focus();
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    setSubmitState(submitBtn, state) {
        if (!submitBtn) return;

        // Usar las traducciones de wp_localize_script si están disponibles
        const i18n = window.GesvaltFormulariosConf?.i18n || {};

        switch (state) {
            case 'loading':
                submitBtn.disabled = true;
                submitBtn.dataset.originalText = submitBtn.textContent;
                submitBtn.textContent = i18n.submit_sending || 'Enviando...';
                submitBtn.classList.add('gesvalt-btn-loading');
                break;

            case 'success':
                submitBtn.disabled = false;
                submitBtn.textContent = i18n.submit_sent || '✓ Enviado';
                submitBtn.classList.remove('gesvalt-btn-loading');
                submitBtn.classList.add('gesvalt-btn-success');
                break;

            case 'error':
                submitBtn.disabled = false;
                submitBtn.textContent = submitBtn.dataset.originalText || i18n.submit_default || 'Enviar';
                submitBtn.classList.remove('gesvalt-btn-loading');
                submitBtn.classList.add('gesvalt-btn-error');
                break;

            default:
                submitBtn.disabled = false;
                submitBtn.textContent = submitBtn.dataset.originalText || i18n.submit_default || 'Enviar';
                submitBtn.classList.remove('gesvalt-btn-loading', 'gesvalt-btn-success', 'gesvalt-btn-error');
        }
    }

    getFieldLabel(field) {
        // Buscar label asociado
        const label = document.querySelector(`label[for="${field.id}"]`);
        if (label) return label.textContent.replace('*', '').trim();

        // Buscar label padre
        const parentLabel = field.closest('label');
        if (parentLabel) return parentLabel.textContent.replace('*', '').trim();

        // Usar placeholder o name como fallback
        return field.placeholder || field.name;
    }

    /**
     * Configura las dependencias de campos en un formulario
     */
    bindFieldDependencies(form) {
        const dependentFields = form.querySelectorAll('[data-depends-on]');

        dependentFields.forEach(field => {
            this.setupFieldDependency(field, form);
        });
    }

    /**
     * Configura la dependencia de un campo específico
     */
    setupFieldDependency(dependentField, form) {
        const dependsOn = dependentField.dataset.dependsOn;
        const dependsVal = dependentField.dataset.dependsVal;

        if (!dependsOn || !dependsVal) return;

        // Buscar el campo padre
        const parentField = form.querySelector(`[name="${dependsOn}"]`);
        if (!parentField) {
            console.warn(`Campo padre "${dependsOn}" no encontrado para dependencia`);
            return;
        }

        // Configurar evento change en el campo padre
        const handleParentChange = () => {
            this.evaluateFieldDependency(dependentField, parentField, dependsVal);
        };

        // Añadir eventos según el tipo de campo padre
        if (parentField.type === 'radio') {
            // Para radio buttons, buscar todos los del mismo nombre
            const radioButtons = form.querySelectorAll(`[name="${dependsOn}"]`);
            radioButtons.forEach(radio => {
                radio.addEventListener('change', handleParentChange);
            });
        } else {
            parentField.addEventListener('change', handleParentChange);
            parentField.addEventListener('input', handleParentChange);
        }

        // Evaluar estado inicial
        this.evaluateFieldDependency(dependentField, parentField, dependsVal);
    }

    /**
     * Evalúa si un campo dependiente debe mostrarse u ocultarse
     */
    evaluateFieldDependency(dependentField, parentField, expectedValue) {
        const parentValue = this.getFieldValue(parentField);
        const shouldShow = this.shouldShowDependentField(parentValue, expectedValue);

        this.toggleFieldVisibility(dependentField, shouldShow);
    }

    /**
     * Determina si un campo dependiente debe mostrarse basándose en el valor del padre
     */
    shouldShowDependentField(parentValue, expectedValue) {
        // Manejar múltiples valores esperados separados por coma
        const expectedValues = expectedValue.split(',').map(val => val.trim());

        return expectedValues.includes(parentValue);
    }

    /**
     * Obtiene el valor de un campo, manejando diferentes tipos
     */
    getFieldValue(field) {
        if (field.type === 'radio') {
            const form = field.closest('form');
            const checkedRadio = form.querySelector(`[name="${field.name}"]:checked`);
            return checkedRadio ? checkedRadio.value : '';
        } else if (field.type === 'checkbox') {
            return field.checked ? field.value : '';
        } else {
            return field.value;
        }
    }

    /**
     * Muestra u oculta un campo y su contenedor
     */
    toggleFieldVisibility(field, show) {
        const container = this.getFieldContainer(field);

        if (show) {
            container.style.display = '';
            container.classList.remove('gesvalt-field-hidden');
        } else {
            container.style.display = 'none';
            container.classList.add('gesvalt-field-hidden');
            // Limpiar el valor del campo cuando se oculta
            this.clearFieldValue(field);
        }
    }

    /**
     * Obtiene el contenedor del campo (wrapper div)
     */
    getFieldContainer(field) {
        // Buscar el contenedor .gesvalt-field-wrapper
        const wrapper = field.closest('.gesvalt-field-wrapper');
        return wrapper || field;
    }

    /**
     * Limpia el valor de un campo
     */
    clearFieldValue(field) {
        if (field.type === 'radio') {
            const form = field.closest('form');
            const radioButtons = form.querySelectorAll(`[name="${field.name}"]`);
            radioButtons.forEach(radio => {
                radio.checked = false;
            });
        } else if (field.type === 'checkbox') {
            field.checked = false;
        } else {
            field.value = '';
        }

        // Limpiar errores del campo
        this.clearFieldError(field);
    }

    /**
     * Verifica si un campo está oculto por dependencias
     */
    isFieldHiddenByDependency(field) {
        const container = this.getFieldContainer(field);
        return container.classList.contains('gesvalt-field-hidden') ||
            container.style.display === 'none';
    }

    observeNewForms() {
        // Observer para formularios añadidos dinámicamente
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) { // Element node
                        // Buscar formularios en el nodo añadido
                        const forms = node.querySelectorAll ? node.querySelectorAll('form[data-gesvalt-form]') : [];
                        forms.forEach(form => this.bindForm(form));

                        // Si el nodo mismo es un formulario
                        if (node.matches && node.matches('form[data-gesvalt-form]')) {
                            this.bindForm(node);
                        }
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    // API pública para validación manual
    validateFormById(formId) {
        const form = document.getElementById(formId);
        return form ? this.validateForm(form) : false;
    }

    addCustomValidationRule(name, validator) {
        this.validationRules[name] = validator;
    }
}

document.addEventListener('DOMContentLoaded', () => {
    // Inicializar automáticamente
    const gesvaltFormHandler = new GesvaltFormHandler();

    // Exportar para uso global
    window.GesvaltFormHandler = GesvaltFormHandler;
    window.gesvaltFormHandler = gesvaltFormHandler;
});
